import {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  useCallback,
} from "react";

import { Currency, PaymentMethod, WithdrawalType } from "../constants";
import { Beneficiary } from "../services/queries/beneficiaries/types";

export interface TransactionState {
  walletId: string;
  withdrawalType: WithdrawalType;
  baseCurrency: Currency;
  baseAmount: string;
  fee: number;
  accountName: string;
  accountNumber: string;
  bankCode: string;
  bankName: string;
  paymentMethod: PaymentMethod;
  saveBeneficiary: boolean;
  balance: number;
  pin: string;
  beneficiaryId?: string | undefined;
  beneficiary?: Beneficiary | undefined;
  quoteCurrency: Currency;
  quoteAmount: string;
  rateId?: string | undefined;
  rate?: number | undefined;
  remarks?: string;
}

export type TransactionContextType = {
  state: TransactionState;
  updateTransaction: (payload: Partial<TransactionState>) => void;
  resetTransaction: () => void;
};

export const SET_TRANSACTION_FORM_DATA = "SET_TRANSACTION_FORM_DATA";
export const RESET_TRANSACTION_FORM_DATA = "RESET_TRANSACTION_FORM_DATA";

export type TransactionAction =
  | {
      type: typeof SET_TRANSACTION_FORM_DATA;
      payload: Partial<TransactionState>;
    }
  | {
      type: typeof RESET_TRANSACTION_FORM_DATA;
    };

const initialState: TransactionState = {
  walletId: "",
  withdrawalType: WithdrawalType.INTERNATIONAL,
  baseCurrency: Currency.NGN,
  baseAmount: "",
  fee: 0,
  accountName: "",
  accountNumber: "",
  bankCode: "",
  bankName: "",
  paymentMethod: PaymentMethod.BANK,
  saveBeneficiary: false,
  balance: 0,
  pin: "",
  beneficiary: undefined,
  beneficiaryId: undefined,
  quoteCurrency: Currency.KES,
  quoteAmount: "",
  rateId: undefined,
  rate: undefined,
  remarks: "",
};

const STORAGE_KEY = "transactionState";

const getInitialState = (): TransactionState => {
  const savedState = localStorage.getItem(STORAGE_KEY);
  return savedState ? JSON.parse(savedState) : initialState;
};

const TransactionContext = createContext<TransactionContextType>({
  state: initialState,
  updateTransaction: () => {},
  resetTransaction: () => {},
});

function reducer(
  state: TransactionState,
  action: TransactionAction,
): TransactionState {
  let newState: TransactionState;

  switch (action.type) {
    case SET_TRANSACTION_FORM_DATA:
      newState = { ...state, ...action.payload };
      break;
    case RESET_TRANSACTION_FORM_DATA:
      newState = initialState;
      break;
    default:
      return state;
  }

  // Persist to localStorage after state changes
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));

  return newState;
}

export default function TransactionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, getInitialState());

  const updateTransaction = useCallback(
    (payload: Partial<TransactionState>) => {
      dispatch({ type: SET_TRANSACTION_FORM_DATA, payload });
    },
    [],
  );

  const resetTransaction = useCallback(() => {
    dispatch({ type: RESET_TRANSACTION_FORM_DATA });
  }, []);

  return (
    <TransactionContext.Provider
      value={{
        state,
        updateTransaction,
        resetTransaction,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
}

export const useTransaction = () => {
  const context = useContext(TransactionContext);
  return context;
};
